<script lang="ts">
	import { Auth } from "@utils/Auth.svelte.js";
	import { getTranslate } from "@utils/getTranslate.js";
	import { isMenuActive } from "@utils/isMenuActive.js";
	import chevronIcon from "@core/assets/icons/chevron.svg?raw";
	import logoutIcon from "@core/assets/icons/logout.svg?raw";
	import { createReactiveBreakpoint } from "@core/utils/createReactiveBreakpoint.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import AuthenticationMenu from "./AuthenticationMenu.svelte";
	import translations from "./ClientZoneNavBar.translations.json" with { type: "json" };
	import { getAuthMenuItems } from "./authMenuItems.js";
	import FFCMemberBadge from "./common/FFCMemberBadge.svelte";
	import Formatted from "./common/Formatted.svelte";
	import Icon from "./common/Icon.svelte";
	import { getScrollY } from "./layout/Header.svelte";

	const { tenant, pagePath, infoBannerMessages } = getGlobalTemplateProps();
	const currentPath = pagePath[tenant.locale];
	const translate = getTranslate(translations);

	let dropdownOpen = $state(false);

	async function signOut(): Promise<void> {
		await Auth.signOut();
		dropdownOpen = false;
	}

	let dialog = $state<HTMLDialogElement>();

	const desktop = createReactiveBreakpoint(">lg");

	$effect(() => {
		if (dropdownOpen) {
			dialog?.showModal();
		} else {
			dialog?.close();
		}
	});

	$effect(() => {
		if (desktop.current && dropdownOpen) {
			dropdownOpen = false;
		}
	});
</script>

<div class="bg-white sticky top-14 lg:top-20 mb-6 lg:mb-14 full-width grid-container z-30">
	<nav class="h-16 flex justify-stretch lg:justify-between">
		<ul class="contents lg:flex gap-10">
			{#each getAuthMenuItems(tenant, Auth.customerType) as item (item)}
				<li class="list-none h-16 lg:flex items-stretch contents">
					{#if currentPath !== undefined && isMenuActive(item, currentPath)}
						<button
							type="button"
							class="flex items-center gap-2 text-fcrdarkgray lg:text-fcrbrown grow lg:grow-0"
							onclick={(): void => {
								dropdownOpen = !dropdownOpen;
							}}
						>
							<Icon icon={item.icon} class="size-6" />
							<span class="font-medium text-base">
								<Formatted text={item.name} />
							</span>
							<Icon
								icon={chevronIcon}
								aria-hidden="true"
								class={["size-6 transition-transform ml-auto lg:hidden", dropdownOpen && "text-fcrbrown rotate-180"]}
							/>
						</button>
					{:else}
						<a
							href={item.href}
							class="hidden lg:flex items-center gap-2 text-fcrdarkgray hover:text-fcrbrown transition-colors"
						>
							<Icon icon={item.icon} class="size-6 text-fcrgray [:hover>&]:text-fcrbrown" />
							<span class="font-medium text-base">
								<Formatted text={item.name} />
							</span>
						</a>
					{/if}
				</li>
			{/each}
		</ul>
		{#if Auth.isFfcMember}
			<FFCMemberBadge class="hidden self-center lg:flex" />
		{/if}
		<button
			type="button"
			class="hidden items-center gap-2 text-fcrdarkgray hover:text-fcrbrown transition-colors lg:flex grow lg:grow-0"
			onclick={(): void => {
				void Auth.signOut();
			}}
		>
			<Icon icon={logoutIcon} class="size-6 text-fcrgray [:hover>&]:text-fcrbrown" />
			<span class="font-medium text-base">
				<Formatted text={translate("signout")} />
			</span>
		</button>
	</nav>
	<hr class="full-width border-fcrlightgray-300 border-solid" />
</div>
<dialog
	class={[
		"backdrop:text-fcrdarkgray/80 backdrop:bg-transparent backdrop:bg-[linear-gradient(transparent_0,transparent_var(--offset),currentColor_var(--offset),currentColor_100%)] mx-[var(--container-gap)] sm:ml-auto sm:w-150 w-full bg-transparent",
		// eslint-disable-next-line @typescript-eslint/no-unsafe-call
		getScrollY() < 1 && infoBannerMessages.length > 0
			? "mt-[calc(9rem+var(--info-banner-height))] [--offset:calc(7.5rem+var(--info-banner-height))]"
			: "mt-36 [--offset:7.5rem]",
	]}
	bind:this={dialog}
	onclose={(): void => {
		dropdownOpen = false;
	}}
	onclick={(event): void => {
		if (event.target === event.currentTarget) {
			dropdownOpen = false;
		}
	}}
>
	<AuthenticationMenu onsignout={signOut} />
</dialog>
